import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';

/**
 * BentoCard - Fundamental building block for the Bento Grid layout
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {'1'|'2'|'full'} [props.span='1'] - Grid column span
 * @param {boolean} [props.hover=true] - Enable hover effects
 * @param {number} [props.delay=0] - Animation delay in milliseconds (for staggered reveal)
 * @param {object} [props.style={}] - Additional inline styles
 */
const BentoCard = ({
  children,
  className = '',
  span = '1',
  hover = true,
  delay = 0,
  style = {}
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const timer = setTimeout(() => setIsLoaded(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Determine grid column span (responsive)
  const gridColumn = span === '2'
    ? 'span 1' // Always span 1 on mobile, span 2 on larger screens
    : span === 'full'
    ? '1 / -1'
    : 'span 1';

  // Additional class for span 2 items to handle responsive
  const spanClass = span === '2' ? 'md:col-span-2' : '';

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border backdrop-blur-sm',
        'transition-all duration-500 ease-out',
        hover && 'hover:border-[var(--color-accent)]/30 hover:shadow-lg hover:shadow-[var(--color-accent)]/5 hover:-translate-y-1',
        spanClass,
        className
      )}
      style={{
        background: 'var(--color-bg-card)',
        borderColor: 'var(--color-border)',
        gridColumn: span === 'full' ? gridColumn : undefined,
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: `${delay}ms`,
        ...style
      }}
    >
      {/* Hover Gradient Effect */}
      {hover && (
        <div
          className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: 'radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0, 212, 170, 0.06), transparent 40%)'
          }}
        />
      )}

      {/* Card Content */}
      {children}
    </div>
  );
};

export default BentoCard;
